import {
  QueryParamsType,
  ProductsQueryOptionsType,
  ProductsPaginationQueryOptionsType,
  Product,
} from '@framework/types';
import { CoreApi, ParamsType } from '@framework/utils/core-api';
import { API_ENDPOINTS } from '@framework/utils/endpoints';
import { mapPaginatorData } from '@framework/utils/data-mappers';
import {
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  useQuery,
} from 'react-query';
import {
  ProductCommonService,
  ProductHotAkcesService,
  BestProductsService,
  NewProductsService,
  BestSellerProductsService,
  ShopProductsService,
  FavoritesProductsService,
  RelatedProductShopsService,
  ProductRelatedByIdService,
  ProductRelatedByCateIdService,
  BestHomeProductsService,
  BestProductsShopService
} from "./product.service";

const ProductService = new CoreApi(API_ENDPOINTS.PRODUCTS);
const ProductServiceDetail = new CoreApi(API_ENDPOINTS.PRODUCT_DETAIL);
const ProductPopupDetail = new CoreApi(API_ENDPOINTS.PRODUCT_POPUP_DETAIL);

type PaginatedProduct = {
  data: Product[];
  paginatorInfo: any;
};

type VoteWishlistsProductType = {
  product_id: number | string;
};

const fetchProducts = async ({
  queryKey,
  pageParam,
}: QueryParamsType): Promise<PaginatedProduct> => {
  const params = queryKey[1];
  let fetchedData: any = {};
  if (pageParam) {
    const response = await ProductService.fetchUrl(pageParam);
    fetchedData = response.data;
  } else {
    const response = await ProductService.find(params as ParamsType);
    fetchedData = response.data;
  }
  const { data, ...rest } = fetchedData;
  return { data, paginatorInfo: mapPaginatorData({ ...rest }) };
};

const useProductsQuery = (
  params: ProductsQueryOptionsType,
  options?: UseInfiniteQueryOptions<
    PaginatedProduct,
    Error,
    PaginatedProduct,
    PaginatedProduct,
    QueryKey
  >
) => {
  return useInfiniteQuery<PaginatedProduct, Error>(
    [API_ENDPOINTS.PRODUCTS, params],
    fetchProducts,
    {
      ...options,
      getNextPageParam: ({ paginatorInfo }) => paginatorInfo.nextPageUrl,
    }
  );
};

export const productObjects = {
  hotProducts: ProductHotAkcesService,
  akceProducts: ProductHotAkcesService,
  bestProducts: BestProductsService,
  newProducts: NewProductsService,
  bestSellerProducts: BestSellerProductsService,
  shopProducts: ShopProductsService,
  favoritesProducts: FavoritesProductsService
}

const fetchPaginationProducts = async (params: ProductsPaginationQueryOptionsType): Promise<{products: Product[]; paginatorInfo: any}> => {
  // @ts-ignore
  const response = await productObjects[params.function]?.all(params);
  const { data, ...rest } = response || {};

  return { products: data, paginatorInfo: mapPaginatorData({ ...rest })};
};

const useProductsPaginationQuery = (
  params: ProductsPaginationQueryOptionsType,
  options: any = {}
) => {
  return useQuery<any, Error>([API_ENDPOINTS.PRODUCTS, params], () => fetchPaginationProducts(params), {
      ...options,
      keepPreviousData : true,
      staleTime: Infinity
    }
  );
};

const fetchBestHomeProducts = async (params: ProductsPaginationQueryOptionsType): Promise<{products: Product[];}> => {
  // @ts-ignore
  const response = await BestHomeProductsService?.all(params);

  return { products: response};
};

const useProductsBesHomeQuery = (
  params: ProductsPaginationQueryOptionsType,
  options: any = {}
) => {
  return useQuery<any, Error>([API_ENDPOINTS.PRODUCTS, params], () => fetchBestHomeProducts(params), {
      ...options,
      keepPreviousData : true,
      staleTime: Infinity
    }
  );
};


const fetchBestProductsShop = async (params: ProductsPaginationQueryOptionsType): Promise<{products: Product[];}> => {
  // @ts-ignore
  const response = await BestProductsShopService?.all(params);
  return { products: response};
};

export const useProductsBestShopQuery = (
  params: ProductsPaginationQueryOptionsType,
  options: any = {}
) => {
  return useQuery<any, Error>([API_ENDPOINTS.FETCH_BEST_PRODUCTS_SHOP, params], () => fetchBestProductsShop(params), {
      ...options,
      keepPreviousData : true,
      staleTime: Infinity
    }
  );
};

export { useProductsQuery, fetchProducts, useProductsPaginationQuery, fetchPaginationProducts, useProductsBesHomeQuery };

export const fetchProduct = async (slug: string) => {
  const { data } = await ProductServiceDetail.findOneProductDetail(slug);
  return data;
};

export const useProductQuery = (slug: string) => {
  return useQuery<Product, Error>([API_ENDPOINTS.PRODUCTS, slug], () =>
    fetchProduct(slug),
    {
      staleTime: Infinity,
    }
  );
};

export const useVoteWishlistsProductMutation = () => {
  return useMutation((input: VoteWishlistsProductType) =>
    ProductCommonService.voteWishlistsProduct(input)
  );
};

export const fetchRelatedProductShop = async (params: any) => {
  const data = await RelatedProductShopsService.all(params);

  return data || [];
};

export const useFetchRelatedProductShopQuery = (params: any, options={}) => {
  return useQuery<any, Error>([API_ENDPOINTS.FETCH_RELATED_PRODUCT_SHOPS, params], () =>
    fetchRelatedProductShop(params),
    {
      ...options,
      staleTime: Infinity,
    }
  );
};

export const fetchProductRelatedById = async (params: any) => {
  const { data } = await ProductRelatedByIdService.fetchRelatedProducts(params);

  return data || [];
};

export const useFetchProductRelatedByIdQuery = (params: any, options={}) => {
  return useQuery<any, Error>([API_ENDPOINTS.GET_PRODUCT_RELATED_BY_ID, params], () =>
  fetchProductRelatedById(params),
    {
      ...options,
      staleTime: Infinity,
    }
  );
};

export const fetchProductRelatedByCateId = async (params: any) => {
  const { data } = await ProductRelatedByCateIdService.fetchRelatedCategoryProducts(params);

  return data || [];
};

export const useFetchProductRelatedByCateIdQuery = (params: any, options={}) => {
  return useQuery<any, Error>([API_ENDPOINTS.GET_PRODUCT_BY_CATEIDS, params], () =>
  fetchProductRelatedByCateId(params),
    {
      ...options,
      staleTime: Infinity,
    }
  );
};

export const fetchProductIds = async (params: any) => {
  const { ids, locale } = params;
  const { data } = await ProductCommonService.fetchProductIds(ids, locale);

  return data || [];
};

export const useFetchProductIdsQuery = (params: any) => {
  return useQuery<any, Error>([API_ENDPOINTS.FETCH_PRODUCT_IDS, params], () =>
    fetchProductIds(params),
    {
      staleTime: Infinity,
    }
  );
};

export const fetchPopupProduct = async (id: string) => {
  const { data } = await ProductPopupDetail.findOneProductDetail(id);
  return data;
};

export const usePopupProductQuery = (id: string, options = {}) => {
  return useQuery<Product, Error>([API_ENDPOINTS.PRODUCT_POPUP_DETAIL, id], () =>
    fetchPopupProduct(id),
    {
      ...options,
      staleTime: Infinity,
    }
  );
};
